/**
 * API Service for communicating with the backend
 * Updated for production deployment
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add authentication
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),

  logout: () =>
    api.post('/auth/logout'),

  getCurrentUser: () =>
    api.get('/auth/me'),

  checkAuth: () =>
    api.get('/auth/check'),
};

// Songs API
export const songsAPI = {
  getSongs: (params = {}) =>
    api.get('/songs', { params }),

  importSongs: (urls) =>
    api.post('/songs/import', { urls }),

  deleteSong: (songId) =>
    api.delete(`/songs/${songId}`),

  streamSong: (songId) =>
    `${API_BASE_URL}/songs/${songId}/stream`,

  downloadSong: (songId) =>
    api.post(`/songs/${songId}/download`),

  archiveSong: (songId) =>
    api.post(`/songs/${songId}/archive`),

  archiveAllSongs: () =>
    api.post('/songs/archive-all'),

  restoreSong: (songId) =>
    api.post(`/songs/${songId}/restore`),

  getArchivedSongs: (params = {}) =>
    api.get('/songs/archived', { params }),

  extractMetadata: (songId) =>
    api.post(`/songs/${songId}/extract-metadata`),
};

// Playback API
export const playbackAPI = {
  getState: () =>
    api.get('/playback/state'),

  updateState: (songId, position, isPlaying) =>
    api.post('/playback/state', {
      song_id: songId,
      position,
      is_playing: isPlaying,
    }),

  recordEvent: (songId, eventType, position) =>
    api.post('/playback/events', {
      song_id: songId,
      event_type: eventType,
      position,
    }),
};

// Favorites API
export const favoritesAPI = {
  getFavorites: () =>
    api.get('/favorites'),

  addFavorite: (songId) =>
    api.post(`/favorites/${songId}`),

  removeFavorite: (songId) =>
    api.delete(`/favorites/${songId}`),
};

// Utility API
export const utilityAPI = {
  getStats: () =>
    api.get('/stats'),

  getRecentlyPlayed: (params = {}) =>
    api.get('/recently-played', { params }),

  getPlaybackState: () =>
    api.get('/playback/state'),

  healthCheck: () =>
    api.get('/health'),
};

// Queue API
export const queueAPI = {
  getQueueStatus: () =>
    api.get('/queue/status'),

  getJobStatus: (jobId) =>
    api.get(`/queue/jobs/${jobId}`),

  retryJob: (jobId) =>
    api.post(`/queue/retry/${jobId}`),
};

// Admin API
export const adminAPI = {
  getSettings: () =>
    api.get('/admin/settings'),

  updateCookies: (cookies) =>
    api.post('/admin/cookies', { cookies }),

  deleteCookies: () =>
    api.delete('/admin/cookies'),

  testDownload: () =>
    api.post('/admin/test-download'),
};

// Cookies API (specialized for cookie management)
export const cookiesAPI = {
  getStatus: () =>
    api.get('/admin/settings'),

  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('cookies', file);
    return api.post('/admin/cookies-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  saveCookies: (content) =>
    api.post('/admin/cookies', { cookies: content }),

  testCookies: () =>
    api.post('/admin/test-cookies'),

  deleteCookies: () =>
    api.delete('/admin/cookies'),
};

export default api;